import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../services/common.service';
import { API_URLS, API_BASE_URL } from '../../constants/api-urls';
import './AcceptRejectProduct.scss';

const API_BASE_URL_MEDIA = API_BASE_URL.replace('/api/v1', '');
const getMediaUrl = (accessPath, auctionId = null) => {
  if (!accessPath) return '';
  const path = typeof accessPath === 'string' ? accessPath : (accessPath && accessPath.path ? accessPath.path : '');
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/auction-media/')) return `${API_BASE_URL_MEDIA}${path}`;
  const fileName = path.split(/[/\\]/).pop();
  if (fileName && auctionId) return `${API_BASE_URL_MEDIA}/auction-media/${auctionId}/${fileName}`;
  return `${API_BASE_URL_MEDIA}/media/${fileName || ''}`;
};
const getFirstProductImageUrl = (auction) => {
  if (!auction?.images) return null;
  let arr = [];
  if (Array.isArray(auction.images)) arr = auction.images;
  else if (typeof auction.images === 'string') {
    try { arr = auction.images.trim().startsWith('[') ? JSON.parse(auction.images) : [auction.images]; } catch (_) { arr = [auction.images]; }
  }
  const first = arr[0];
  if (!first) return null;
  return getMediaUrl(first, auction.auction_id);
};

export default function AcceptRejectProduct() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const [inspection, setInspection] = useState(null);
  const [auction, setAuction] = useState(null);
  const [winningBid, setWinningBid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [decision, setDecision] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchData();
  }, [auctionId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch inspection by auction
      const inspectionResponse = await CommonService.get(API_URLS.AUCTION.GET_INSPECTION_BY_AUCTION(auctionId));
      if (inspectionResponse && inspectionResponse.success && inspectionResponse.data) {
        setInspection(inspectionResponse.data);
      }
      
      // Fetch auction
      const auctionResponse = await CommonService.get(`${API_URLS.AUCTION.BASE}/${auctionId}`);
      if (auctionResponse && auctionResponse.success) {
        setAuction(auctionResponse.data);
        
        // Fetch winning bid
        const bidsResponse = await CommonService.get(API_URLS.AUCTION.BIDS(auctionId));
        if (bidsResponse && bidsResponse.success && bidsResponse.data) {
          const bids = Array.isArray(bidsResponse.data) ? bidsResponse.data : [];
          const bid = bids.find(b => 
            b.bid_id === auctionResponse.data.winning_bid_id || 
            b.bid_status === 'won'
          );
          if (bid) {
            setWinningBid(bid);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load inspection data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!decision) {
      toast.error('Please make a decision');
      return;
    }

    if (decision === 'reject' && !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      setSubmitting(true);
      
      const payload = {
        inspection_id: inspection.inspection_id,
        decision: decision,
        rejection_reason: decision === 'reject' ? rejectionReason : null
      };

      const response = await CommonService.post(API_URLS.AUCTION.CREATE_INSPECTION_DECISION, payload);
      
      if (response && response.success) {
        toast.success(`Product ${decision === 'accept' ? 'accepted' : 'rejected'} successfully!`);
        if (decision === 'accept') {
          navigate(`/auction/${auctionId}/collection-delivery`);
        } else {
          navigate(`/auction/${auctionId}/product-rejected`);
        }
      } else {
        throw new Error(response?.message || 'Failed to submit decision');
      }
    } catch (error) {
      console.error('Error submitting decision:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to submit decision');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTimeRemaining = () => {
    if (!inspection?.sla_deadline) return '72h 30m';
    const deadline = new Date(inspection.sla_deadline);
    const now = new Date();
    const diff = deadline - now;
    
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return <div className="accept-reject-product loading">Loading...</div>;
  }

  if (!inspection || !auction) {
    return <div className="accept-reject-product error">Inspection or auction not found</div>;
  }

  const reportItems = inspection.inspection_report && typeof inspection.inspection_report === 'string'
    ? JSON.parse(inspection.inspection_report)
    : inspection.inspection_report || { items: [] };

  return (
    <div className="accept-reject-product">
      <div className="container-fluid">
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
        {/* Success Banner */}
        <div className="success-banner">
          <div className="banner-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="24" fill="#28a745"/>
              <path d="M15 24L21 30L33 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="banner-content">
            <h1>Inspection Completed Successfully!</h1>
            <p>The product inspection has been completed. Review the inspection report and decide whether to accept or reject the product.</p>
          </div>
          <div className="banner-info">
            <div className="info-item">
              <span className="label">Overall Rating:</span>
              <span className="value">{inspection.overall_rating || 'Good'}</span>
            </div>
            <div className="info-item">
              <span className="label">Inspector:</span>
              <span className="value">{inspection.inspector_name || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Inspection Date:</span>
              <span className="value">{formatDate(inspection.inspection_date)}</span>
            </div>
          </div>
        </div>

        <div className="content-layout">
          {/* Left Column */}
          <div className="left-column">
            {/* Product Details */}
            <div className="section-card product-card">
              <div className="section-header">
                <div className="section-icon orange">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h3>Product Details</h3>
              </div>
              <div className="product-image">
                {getFirstProductImageUrl(auction) ? (
                  <img src={getFirstProductImageUrl(auction)} alt={auction.product_name} />
                ) : (
                  <div className="placeholder-image">No Image</div>
                )}
              </div>
              <div className="product-name">{auction.product_name}</div>
              <div className="product-category">Category: {auction.category_name || 'Vehicles'}</div>
              <div className="product-info-grid">
                <div className="info-item">
                  <span className="label">Your Winning Bid:</span>
                  <span className="value">{formatCurrency(winningBid?.bid_amount || 0)}</span>
                </div>
                <div className="info-item status-completed">
                  <span className="label">Inspection Status:</span>
                  <span className="value">Completed</span>
                </div>
                <div className="info-item">
                  <span className="label">Location:</span>
                  <span className="value">{auction.seller_company || 'N/A'} - {auction.city || ''}</span>
                </div>
                <div className="info-item status-next">
                  <span className="label">Next Step:</span>
                  <span className="value">Accept or Reject</span>
                </div>
              </div>
            </div>

            {/* Inspection Report Summary */}
            <div className="section-card report-card">
              <div className="section-header">
                <div className="section-icon blue">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h3>Inspection Report Summary</h3>
              </div>
              <div className="report-items">
                {reportItems.items && reportItems.items.map((item, index) => (
                  <div key={index} className={`report-item ${item.status}`}>
                    <div className="item-header">
                      <span className="item-name">{item.name}</span>
                      <span className={`item-status-badge ${item.status}`}>
                        {item.status === 'passed' && '✓ Passed'}
                        {item.status === 'minor_issues' && '⚠ Minor Issues'}
                        {item.status === 'failed' && '✗ Failed'}
                      </span>
                    </div>
                    <div className="item-notes">{item.notes || 'N/A'}</div>
                  </div>
                ))}
              </div>
              {inspection.inspector_recommendations && (
                <div className="recommendations">
                  <h4>Inspector Recommendations:</h4>
                  <ul>
                    {inspection.inspector_recommendations.split('\n').map((rec, idx) => (
                      <li key={idx}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
              <button
                type="button"
                className="btn-view-report"
                onClick={() => navigate(`/auction/inspection/${inspection.inspection_id}/complete`)}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 2C4.667 2 2 4.667 2 8C2 11.333 4.667 14 8 14C11.333 14 14 11.333 14 8C14 4.667 11.333 2 8 2ZM8 12C5.8 12 4 10.2 4 8C4 5.8 5.8 4 8 4C10.2 4 12 5.8 12 8C12 10.2 10.2 12 8 12Z" fill="currentColor"/>
                  <path d="M8 6V8M8 10H8.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                View Detailed Report
              </button>
            </div>
          </div>

          {/* Right Column */}
          <div className="right-column">
            {/* Your Decision */}
            <div className="section-card decision-card">
              <h3>Your Decision</h3>
              <div className="decision-prompt">
                <div className="prompt-header">
                  <span className="label">Decision Required</span>
                </div>
                <p>You have 72 hours to accept or reject the product based on the inspection report.</p>
                <div className="time-remaining">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2"/>
                    <polyline points="8 4 8 8 11 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>{getTimeRemaining()}</span>
                </div>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="decision-buttons">
                  <button
                    type="button"
                    className={`btn-accept ${decision === 'accept' ? 'active' : ''}`}
                    onClick={() => setDecision('accept')}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M16.667 5L7.5 14.167 3.333 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Accept Product & Confirm Handover
                  </button>
                  <button
                    type="button"
                    className={`btn-reject ${decision === 'reject' ? 'active' : ''}`}
                    onClick={() => setDecision('reject')}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Reject Product
                  </button>
                </div>

                {decision === 'reject' && (
                  <div className="rejection-reason">
                    <label>Rejection Reason <span className="required">*</span></label>
                    <textarea
                      className="form-control"
                      rows="4"
                      placeholder="Please provide a reason for rejecting the product..."
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      required
                    />
                  </div>
                )}

                {decision && (
                  <button type="submit" className="btn-submit" disabled={submitting}>
                    {submitting ? 'Processing...' : `Confirm ${decision === 'accept' ? 'Acceptance' : 'Rejection'}`}
                  </button>
                )}
              </form>
            </div>

            {/* What happens after acceptance */}
            {decision === 'accept' && (
              <div className="section-card after-acceptance-card">
                <h3>What happens after acceptance?</h3>
                <ul className="checklist">
                  <li>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M13.333 4L6 11.333 2.667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Payment will be released to seller
                  </li>
                  <li>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M13.333 4L6 11.333 2.667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Seller will be notified for delivery
                  </li>
                  <li>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M13.333 4L6 11.333 2.667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    You can coordinate pickup details
                  </li>
                  <li>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M13.333 4L6 11.333 2.667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Transaction will be completed
                  </li>
                </ul>
              </div>
            )}

            {/* Important Notice */}
            <div className="section-card notice-card">
              <div className="notice-header">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2ZM11 14H9V12H11V14ZM11 10H9V6H11V10Z" fill="currentColor"/>
                </svg>
                <h3>Important Notice</h3>
              </div>
              <p>
                Inspection report is provided for your reference. Final decision to accept or reject the product is yours. 
                Trocanet facilitates the transaction but does not guarantee product condition.
              </p>
            </div>

            {/* Need Help */}
            <div className="section-card help-card">
              <h3>Need Help?</h3>
              <p>Contact our support team if you have questions about the inspection report.</p>
              <button className="btn-contact-support">Contact Support</button>
            </div>

            {/* Next Steps Timeline */}
            <div className="section-card timeline-card">
              <div className="section-header">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="2"/>
                  <polyline points="10 4 10 10 13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <h3>Next Steps Timeline</h3>
              </div>
              <div className="timeline-steps">
                <div className="timeline-step active">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <div className="step-title">Accept or Reject Product</div>
                    <div className="step-description">Review inspection report and make your decision within 72 hours</div>
                  </div>
                </div>
                <div className="timeline-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <div className="step-title">Payment Processing</div>
                    <div className="step-description">Payment will be released to seller after acceptance</div>
                  </div>
                </div>
                <div className="timeline-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <div className="step-title">Delivery Coordination</div>
                    <div className="step-description">Arrange pickup or delivery with seller</div>
                  </div>
                </div>
                <div className="timeline-step">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <div className="step-title">Product Handover</div>
                    <div className="step-description">Complete transaction and confirm receipt</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
